import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import { Avatar, Box, Button, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { BaseModalComponent } from "@components/common/BaseModalComponent";
import { JobApproved } from "@components/jobs/jobs-list/jobs-layout/components/Approved/JobApproved";
import { colors, alphaVar } from "@components/ui/colors";
import { Job, WorkforceMember } from "@components/jobs/interfaces/job.interfaces";
import { useModals } from "src/context/ModalContext";

interface Props {
  job: Job;
}

type SelectedWorkforce = {
  supervisors?: WorkforceMember[];
  employees?: WorkforceMember[];
  contractors?: WorkforceMember[];
};

type WorkforceRole = "supervisor" | "employee" | "contractor";

export function JobDefaultWorkforceSection({ job }: Props) {
  const queryClient = useQueryClient();
  const { closeByKey } = useModals();
  const modalKey = `JobDefaultWorkforce-${job.id}`;

  const [localDefaultWorkforce, setLocalDefaultWorkforce] = useState<WorkforceMember[]>(job.defaultWorkforce ?? []);
  const [showAllWorkforce, setShowAllWorkforce] = useState(false);

  useEffect(() => {
    setLocalDefaultWorkforce(Array.isArray(job.defaultWorkforce) ? job.defaultWorkforce : []);
  }, [job.defaultWorkforce]);

  const workforce = localDefaultWorkforce;

  const activeWorkforce = workforce.filter((worker) => {
    const assignment = (worker as WorkforceMember & { WorkforcesByJobs?: { isDeleted?: boolean } }).WorkforcesByJobs ?? worker.WorkforcesByJobEvents;

    return assignment?.isDeleted !== true;
  });

  const hasWorkforce = activeWorkforce.length > 0;

  const visibleLimit = 3;
  const previewWorkers = showAllWorkforce ? activeWorkforce : activeWorkforce.slice(0, visibleLimit);
  const extraCount = Math.max(activeWorkforce.length - visibleLimit, 0);

  const getFullName = (worker: WorkforceMember) => `${worker.firstName ?? ""} ${worker.lastName ?? ""}`.trim();

  const getInitials = (worker: WorkforceMember) => {
    const first = worker.firstName?.charAt(0) ?? "";
    const last = worker.lastName?.charAt(0) ?? "";

    return `${first}${last}`.trim() || "?";
  };

  const isWorkforceRole = (role: unknown): role is WorkforceRole => role === "supervisor" || role === "employee" || role === "contractor";

  const getWorkerRole = (worker: WorkforceMember): WorkforceRole => {
    const workerWithRelations = worker as WorkforceMember & {
      WorkforcesByJobs?: { jobWorkforceRole?: WorkforceRole };
      WorkforcesByJobEvents?: { jobWorkforceRole?: WorkforceRole };
    };

    const roleFromWorker = worker.workforceRole;

    return (
      workerWithRelations.WorkforcesByJobs?.jobWorkforceRole ??
      workerWithRelations.WorkforcesByJobEvents?.jobWorkforceRole ??
      (isWorkforceRole(roleFromWorker) ? roleFromWorker : undefined) ??
      "employee"
    );
  };

  const roleLabel: Record<WorkforceRole, string> = {
    supervisor: "Supervisor",
    employee: "Employee",
    contractor: "Contractor",
  };

  const roleCounts = activeWorkforce.reduce(
    (acc, worker) => {
      const role = getWorkerRole(worker);

      acc[role] += 1;

      return acc;
    },
    {
      supervisor: 0,
      employee: 0,
      contractor: 0,
    } as Record<WorkforceRole, number>,
  );

  const roleSummary = (Object.entries(roleCounts) as [WorkforceRole, number][])
    .filter(([, count]) => count > 0)
    .map(([role, count]) => `${roleLabel[role]} ${count}`)
    .join(" · ");

  const normalizeSavedWorker = (worker: WorkforceMember, role: WorkforceRole): WorkforceMember => {
    return {
      ...worker,
      workforceRole: worker.workforceRole ?? role,
      WorkforcesByJobs: {
        ...(worker as WorkforceMember & { WorkforcesByJobs?: Record<string, unknown> }).WorkforcesByJobs,
        jobWorkforceRole: role,
        isDeleted: false,
      },
    } as WorkforceMember;
  };

  const handleSaveDefaultWorkforce = async (selectedWorkforce?: SelectedWorkforce) => {
    if (selectedWorkforce) {
      const nextDefaultWorkforce = [
        ...(selectedWorkforce.supervisors ?? []).map((worker) => normalizeSavedWorker(worker, "supervisor")),
        ...(selectedWorkforce.employees ?? []).map((worker) => normalizeSavedWorker(worker, "employee")),
        ...(selectedWorkforce.contractors ?? []).map((worker) => normalizeSavedWorker(worker, "contractor")),
      ];

      setLocalDefaultWorkforce(nextDefaultWorkforce);
      setShowAllWorkforce(false);
    }

    await queryClient.invalidateQueries();

    closeByKey(modalKey);
  };

  return (
    <Box
      sx={{
        mx: 3,
        px: { xs: 1.5, sm: 2 },
        py: { xs: 1.5, sm: 1.35 },
        borderRadius: 3,
        bgcolor: hasWorkforce ? alphaVar(colors.orange[100], 0.28) : "var(--fx-surface-base, #FFFFFF)",
        border: `1px solid ${hasWorkforce ? alphaVar(colors.orange[400], 0.18) : null}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          minWidth: 0,
          flex: 1,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2.5,
            bgcolor: hasWorkforce ? alphaVar(colors.orange[100], 0.85) : alphaVar(colors.orange[100], 0.55),
            color: colors.orange[600],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <GroupsRoundedIcon sx={{ fontSize: 20 }} />
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              flexWrap: "wrap",
              rowGap: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.9rem",
                fontWeight: 900,
                color: colors.gray[800],
                lineHeight: 1.2,
              }}
            >
              Job workforce
            </Typography>

            {hasWorkforce && (
              <Box
                sx={{
                  px: 0.9,
                  py: 0.25,
                  borderRadius: "999px",
                  bgcolor: alphaVar(colors.orange[100], 0.85),
                  color: colors.orange[700],
                  fontSize: "0.7rem",
                  fontWeight: 900,
                  lineHeight: 1.2,
                }}
              >
                {activeWorkforce.length} selected
              </Box>
            )}
          </Stack>

          <Typography
            sx={{
              mt: 0.35,
              fontSize: "0.74rem",
              fontWeight: 700,
              color: hasWorkforce ? colors.gray[600] : colors.gray[400],
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: { xs: 220, sm: 520 },
            }}
          >
            {hasWorkforce ? roleSummary : "No default workforce selected"}
          </Typography>

          {hasWorkforce && (
            <Stack
              direction="row"
              spacing={0.75}
              sx={{
                mt: 0.9,
                flexWrap: showAllWorkforce ? "wrap" : "nowrap",
                rowGap: 0.75,
                overflow: "hidden",
                maxWidth: "100%",
                alignItems: "center",
              }}
            >
              {previewWorkers.map((worker) => {
                const role = getWorkerRole(worker);
                const fullName = getFullName(worker);

                return (
                  <Box
                    key={worker.uid}
                    title={fullName}
                    sx={{
                      height: 30,
                      px: 0.6,
                      pr: 1,
                      borderRadius: "999px",
                      bgcolor: "var(--fx-surface-base, #FFFFFF)",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.65,
                      maxWidth: 165,
                      minWidth: 0,
                      flexShrink: 0,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 22,
                        height: 22,
                        fontSize: "0.68rem",
                        fontWeight: 900,
                        bgcolor: alphaVar(colors.orange[100], 0.9),
                        color: colors.orange[700],
                        flexShrink: 0,
                      }}
                    >
                      {getInitials(worker)}
                    </Avatar>

                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 800,
                        color: colors.gray[700],
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                      }}
                    >
                      {fullName || "Unnamed"}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "0.64rem",
                        fontWeight: 900,
                        color: colors.gray[400],
                        textTransform: "uppercase",
                        flexShrink: 0,
                      }}
                    >
                      {roleLabel[role]}
                    </Typography>
                  </Box>
                );
              })}

              {extraCount > 0 && !showAllWorkforce && (
                <Button
                  size="small"
                  onClick={() => setShowAllWorkforce(true)}
                  sx={{
                    height: 30,
                    minWidth: "auto",
                    px: 1,
                    borderRadius: "999px",
                    bgcolor: alphaVar(colors.orange[100], 0.7),
                    color: colors.orange[700],
                    textTransform: "none",
                    fontSize: "0.72rem",
                    fontWeight: 900,
                    flexShrink: 0,
                    "&:hover": {
                      bgcolor: colors.orange[100],
                    },
                  }}
                >
                  +{extraCount} more
                </Button>
              )}

              {showAllWorkforce && activeWorkforce.length > visibleLimit && (
                <Button
                  size="small"
                  onClick={() => setShowAllWorkforce(false)}
                  sx={{
                    height: 30,
                    minWidth: "auto",
                    px: 1,
                    borderRadius: "999px",
                    bgcolor: alphaVar(colors.gray[100], 0.8),
                    color: colors.gray[600],
                    textTransform: "none",
                    fontSize: "0.72rem",
                    fontWeight: 900,
                    flexShrink: 0,
                    "&:hover": {
                      bgcolor: colors.gray[100],
                    },
                  }}
                >
                  Show less
                </Button>
              )}
            </Stack>
          )}
        </Box>
      </Box>

      <BaseModalComponent
        modalKey={modalKey}
        modalLabel="Job workforce"
        defaultSizes={{
          width: "90%",
          height: "80%",
        }}
        preventParentTrigger
        elementTrigger={
          <Button
            variant="text"
            size="small"
            startIcon={<ManageAccountsRoundedIcon sx={{ fontSize: "15px !important" }} />}
            sx={{
              height: 34,
              borderRadius: "999px",
              fontSize: "0.75rem",
              fontWeight: 900,
              textTransform: "none",
              color: colors.orange[700],
              bgcolor: alphaVar(colors.orange[100], 0.7),
              px: 1.6,
              flexShrink: 0,
              transition: "all 0.15s ease",
              "&:hover": {
                bgcolor: colors.orange[100],
                color: colors.orange[700],
              },
              "&:active": {
                transform: "scale(0.97)",
              },
            }}
          >
            Manage
          </Button>
        }
      >
        <JobApproved
          key={job.updatedAt}
          data={job}
          mode="edit"
          saveMode="job-default"
          jobId={job.id}
          currentWf={activeWorkforce}
          onSaveWorkforce={handleSaveDefaultWorkforce}
        />
      </BaseModalComponent>
    </Box>
  );
}
